import { sanitizeSingleLineText } from "./textSecurity";

const DEFAULT_FORWARD_GEOCODE_ENDPOINT = "https://nominatim.openstreetmap.org/search";
// Autocompletion des villes : API Geo de l'Etat (communes francaises,
// recherche par debut de nom, sans cle). OpenStreetMap ne cherche pas par
// debut de mot et renvoyait des villes etrangeres (« Gren » -> Norvege).
const CITY_AUTOCOMPLETE_ENDPOINT = "https://geo.api.gouv.fr/communes";
const forwardGeocodeCache = new Map();
const autocompleteCache = new Map();

function getForwardGeocodeEndpoint() {
  return String(process.env.REACT_APP_FORWARD_GEOCODE_URL || "").trim();
}

function normalizeQuery(value) {
  if (typeof value !== "string" && typeof value !== "number") {
    return "";
  }

  return sanitizeSingleLineText(String(value || ""));
}

function buildCacheKey(query) {
  return query.toLowerCase();
}

function normalizeCoordinates(lng, lat) {
  const safeLng = Number(lng);
  const safeLat = Number(lat);

  if (!Number.isFinite(safeLng) || !Number.isFinite(safeLat)) {
    return null;
  }

  return [safeLng, safeLat];
}

function buildShortLabel(payload = {}) {
  const name = normalizeQuery(payload?.name ?? "");
  const addressObj =
    payload?.address && Object.getPrototypeOf(payload.address) === Object.prototype
      ? payload.address
      : {};
  const city = normalizeQuery(
    addressObj.city ?? addressObj.town ?? addressObj.village ?? addressObj.municipality ?? ""
  );

  if (name && city) return `${name}, ${city}`;
  if (name) return name;
  return normalizeQuery(payload?.display_name ?? payload?.label ?? "");
}

function normalizeForwardResult(payload = {}) {
  const coordinates = normalizeCoordinates(
    payload?.lng ?? payload?.lon ?? payload?.longitude,
    payload?.lat ?? payload?.latitude
  );
  const label = buildShortLabel(payload);

  return { coordinates, label, resolved: Boolean(coordinates) };
}

async function fetchCustomForwardGeocode(endpoint, query) {
  const response = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query }),
  });

  if (!response.ok) {
    throw new Error(`Forward geocoding error: ${response.status}`);
  }

  return response.json();
}

async function fetchDefaultForwardGeocode(query) {
  const params = new URLSearchParams({
    q: query,
    format: "jsonv2",
    addressdetails: "1",
    limit: "1",
  });
  const response = await fetch(
    `${DEFAULT_FORWARD_GEOCODE_ENDPOINT}?${params.toString()}`,
    { headers: { Accept: "application/json" } }
  );

  if (!response.ok) {
    throw new Error(`Forward geocoding error: ${response.status}`);
  }

  return response.json();
}

function normalizeAutocompleteResults(payload) {
  return (Array.isArray(payload) ? payload : [payload])
    .map((item) => {
      const { coordinates, label, resolved } = normalizeForwardResult(item);
      if (!resolved) return null;
      const addressObj =
        item?.address && Object.getPrototypeOf(item.address) === Object.prototype
          ? item.address
          : {};
      const city = normalizeQuery(
        addressObj.city ?? addressObj.town ?? addressObj.village ?? addressObj.municipality ?? ""
      );
      const country = normalizeQuery(addressObj.country ?? "");
      const sublabel = [city, country].filter(Boolean).join(", ");
      return { coordinates, label, sublabel, city };
    })
    .filter(Boolean);
}

// Communes (API Geo) au format de l'autocompletion : centre GeoJSON [lng, lat].
function normalizeCommuneResults(payload) {
  return (Array.isArray(payload) ? payload : [])
    .map((commune) => {
      const city = normalizeQuery(commune?.nom ?? "");
      const coordinates = normalizeCoordinates(commune?.centre?.coordinates?.[0], commune?.centre?.coordinates?.[1]);
      if (!city || !coordinates) return null;
      const department = normalizeQuery(commune?.departement?.nom ?? "");
      return {
        coordinates,
        label: department ? `${city}, ${department}` : city,
        sublabel: department,
        city,
      };
    })
    .filter(Boolean);
}

export async function forwardGeocodeAutocomplete(query, { limit = 5 } = {}) {
  const normalizedQuery = normalizeQuery(query);
  if (!normalizedQuery || normalizedQuery.length < 2) return [];

  const cacheKey = `ac:${normalizedQuery.toLowerCase()}`;
  if (autocompleteCache.has(cacheKey)) return autocompleteCache.get(cacheKey);

  const endpoint = getForwardGeocodeEndpoint();

  try {
    let payload;
    if (endpoint) {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: normalizedQuery, limit }),
      });
      if (!response.ok) throw new Error(`Forward geocoding error: ${response.status}`);
      payload = await response.json();
      const results = normalizeAutocompleteResults(payload);
      autocompleteCache.set(cacheKey, results);
      return results;
    }

    const params = new URLSearchParams({
      nom: normalizedQuery,
      fields: "nom,centre,departement",
      boost: "population",
      limit: String(limit),
    });
    const response = await fetch(
      `${CITY_AUTOCOMPLETE_ENDPOINT}?${params.toString()}`,
      { headers: { Accept: "application/json" } }
    );
    if (!response.ok) throw new Error(`City autocomplete error: ${response.status}`);
    const results = normalizeCommuneResults(await response.json());
    autocompleteCache.set(cacheKey, results);
    return results;
  } catch (error) {
    console.error(error);
    return [];
  }
}

export async function forwardGeocodeAddress(query) {
  const normalizedQuery = normalizeQuery(query);

  if (!normalizedQuery) {
    return { coordinates: null, label: "", resolved: false, source: "empty" };
  }

  const cacheKey = buildCacheKey(normalizedQuery);

  if (forwardGeocodeCache.has(cacheKey)) {
    return forwardGeocodeCache.get(cacheKey);
  }

  const endpoint = getForwardGeocodeEndpoint();

  try {
    const payload = endpoint
      ? await fetchCustomForwardGeocode(endpoint, normalizedQuery)
      : await fetchDefaultForwardGeocode(normalizedQuery);
    const firstResult = Array.isArray(payload) ? payload[0] : payload;
    const { coordinates, label, resolved } = normalizeForwardResult(firstResult);

    const result = {
      coordinates,
      label,
      resolved,
      source: endpoint ? "remote" : "public",
    };

    forwardGeocodeCache.set(cacheKey, result);

    return result;
  } catch (error) {
    console.error(error);
    return { coordinates: null, label: "", resolved: false, source: "fallback" };
  }
}
